import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiX } from "react-icons/fi";
import { useHomepageQuery } from "../../store/services/homepageService";

const normalizeList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (typeof value === "object") return Object.values(value).flat().filter(Boolean);
  return [];
};

const normalizeObject = (value) => {
  if (!value) return {};

  if (typeof value === "string") {
    try { return JSON.parse(value); } catch { return {}; }
  }

  return typeof value === "object" && !Array.isArray(value) ? value : {};
};

const getStorageKey = (campaign) => {
  const rules = normalizeObject(campaign?.display_rules);
  return rules.storage_key || `jooti_campaign_${campaign?.type || "topbar"}_${campaign?.id}`;
};

const isExternalLink = (value) => /^https?:\/\//i.test(String(value || ""));

const imageUrl = (value) => {
  if (!value) return "";
  const image = String(value).trim();
  if (!image) return "";
  if (image.startsWith("http://") || image.startsWith("https://") || image.startsWith("data:")) return image;
  if (image.startsWith("/storage/") || image.startsWith("/images/") || image.startsWith("/assets/")) return image;
  if (image.startsWith("storage/")) return `/${image}`;
  return `/storage/${image}`;
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 760px)").matches;
  });

  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const update = () => setIsMobile(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  return isMobile;
};

const readClosedKeys = () => {
  try {
    const raw = localStorage.getItem("jooti_closed_topbars");
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

const writeClosedKeys = (keys) => {
  try { localStorage.setItem("jooti_closed_topbars", JSON.stringify(keys)); } catch {}
};

const placementMatches = (campaign) => {
  const placement = campaign?.placement || "";
  return !placement || placement === "global" || placement === "all" || placement === "header" || placement === "topbar";
};

const CampaignButton = ({ campaign }) => {
  if (!campaign?.button_text || !campaign?.button_link) return null;
  if (isExternalLink(campaign.button_link)) {
    return <a href={campaign.button_link} className="campaign-topbar-link" target="_blank" rel="noreferrer">{campaign.button_text}</a>;
  }
  return <Link to={campaign.button_link} className="campaign-topbar-link">{campaign.button_text}</Link>;
};

const CampaignImageWrapper = ({ campaign, children }) => {
  if (!campaign?.button_link) return <div className="campaign-topbar-full-image-link">{children}</div>;
  if (isExternalLink(campaign.button_link)) return <a href={campaign.button_link} className="campaign-topbar-full-image-link" target="_blank" rel="noreferrer" aria-label={campaign.title || campaign.name || "بنر جوتی"}>{children}</a>;
  return <Link to={campaign.button_link} className="campaign-topbar-full-image-link" aria-label={campaign.title || campaign.name || "بنر جوتی"}>{children}</Link>;
};

const GlobalTopbar = () => {
  const { data } = useHomepageQuery();
  const isMobile = useIsMobile();
  const [closedKeys, setClosedKeys] = useState(readClosedKeys);

  const activeTopbar = useMemo(() => {
    const topbars = normalizeList(data?.campaigns?.topbar)
      .filter((campaign) => campaign && campaign.is_active !== false)
      .filter(placementMatches)
      .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
    return topbars.find((campaign) => !closedKeys.includes(getStorageKey(campaign))) || null;
  }, [data?.campaigns?.topbar, closedKeys]);

  useEffect(() => {
    if (activeTopbar) { document.body.classList.add("jooti-topbar-active"); }
    else { document.body.classList.remove("jooti-topbar-active"); }
    return () => document.body.classList.remove("jooti-topbar-active");
  }, [activeTopbar]);

  if (!activeTopbar) return null;

  const style = normalizeObject(activeTopbar.style_settings);
  const rules = normalizeObject(activeTopbar.display_rules);
  const tone = style.tone || "soft";
  const dismissible = rules.dismissible !== false && style.show_close !== false;

  const desktopImage = imageUrl(activeTopbar.image);
  const mobileImage = imageUrl(activeTopbar.mobile_image);
  // اصلاح شده: همیشه تصویر دسکتاپ fallback باشد
  const selectedImage = (isMobile && mobileImage) || desktopImage || mobileImage;

  const imageMode = style.image_mode || "icon";
  const isFullImage = Boolean(selectedImage) && ["full_image_tall","full_image","full","image"].includes(imageMode);
  const hasBackgroundImage = Boolean(selectedImage) && imageMode === "background";
  const hasInlineImage = Boolean(selectedImage) && imageMode === "icon";

  const inlineStyle = {
    "--campaign-topbar-bg": style.background_color || style.background || "",
    "--campaign-topbar-color": style.text_color || style.color || "",
    "--campaign-topbar-image": hasBackgroundImage ? `url("${selectedImage}")` : "none"
  };

  const closeTopbar = () => {
    const key = getStorageKey(activeTopbar);
    const next = Array.from(new Set([...closedKeys, key]));
    setClosedKeys(next);
    writeClosedKeys(next);
  };

  if (isFullImage) {
    return (
      <div className={`global-campaign-topbar campaign-topbar-full-image-wrap campaign-tone-${tone}`} style={inlineStyle}>
        <CampaignImageWrapper campaign={activeTopbar}>
          <picture>
            {mobileImage ? <source media="(max-width: 760px)" srcSet={mobileImage} /> : null}
            <img src={selectedImage} alt={activeTopbar.title || activeTopbar.name || "بنر جوتی"} />
          </picture>
        </CampaignImageWrapper>
        {dismissible ? <button type="button" className="campaign-close-button campaign-topbar-full-close" onClick={closeTopbar} aria-label="بستن اعلان"><FiX size={15} /></button> : null}
      </div>
    );
  }

  return (
    <div className="global-campaign-topbar">
      <div className={`campaign-topbar campaign-tone-${tone} ${hasInlineImage?"campaign-topbar-has-image":""} ${hasBackgroundImage?"campaign-topbar-bg-image":""}`} style={inlineStyle}>
        <div className="site-container campaign-topbar-inner">
          <div className="campaign-topbar-main">
            {hasInlineImage ? <span className="campaign-topbar-media"><img src={selectedImage} alt={activeTopbar.title || activeTopbar.name || "اطلاعیه جوتی"} /></span> : null}
            <div className="campaign-topbar-copy">{activeTopbar.title ? <strong>{activeTopbar.title}</strong> : null}{activeTopbar.subtitle ? <span>{activeTopbar.subtitle}</span> : null}</div>
          </div>
          <div className="campaign-topbar-actions">
            <CampaignButton campaign={activeTopbar} />
            {dismissible ? <button type="button" className="campaign-close-button" onClick={closeTopbar} aria-label="بستن اعلان"><FiX size={16} /></button> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalTopbar;
